import {
  Box,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import React, { FC, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { ExtendedFieldError } from "vmd-types";

import { StatusEntry } from "./status-fields";

/**
 * An entry for semaphore status with three threshold colors.
 */
export interface SemaphoreEntry {
  name: string;
  label: string;
  placeholder: string;
  color?: string;
}

export interface SemaphoreFieldsProps {
  /**
   * Initial overall reporting level.
   */
  initialReportingLevel?: "overall" | "category";
  /**
   * Initial status category entries.
   */
  initialEntries?: SemaphoreEntry[];

  initialStatusEntries?: StatusEntry[];
  /**
   * Called when entries (add/remove/update) change.
   */
  onEntriesChange?: (data: {
    entries: SemaphoreEntry[];
    level: "overall" | "category" | undefined;
  }) => void;
  onStatusEntriesChange?: (entries: StatusEntry[]) => void;
  title?: string;
  enableColorPicker?: boolean;
  validationErrors?: Record<string, ExtendedFieldError>;
}

export const SemaphoreFields: FC<SemaphoreFieldsProps> = ({
  initialReportingLevel,
  initialEntries = [],
  initialStatusEntries = [],
  onEntriesChange,
  title,
  enableColorPicker,
  validationErrors,
}) => {
  const theme = useTheme();
  const [reportingLevel, setReportingLevel] = useState<
    "overall" | "category" | undefined
  >(initialReportingLevel);
  const [entries, setEntries] = useState<SemaphoreEntry[] | StatusEntry[]>(
    reportingLevel === "category" ? initialEntries : initialStatusEntries
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const updateEntries = (
    newEntries: SemaphoreEntry[],
    level: "overall" | "category" | undefined
  ) => {
    setEntries(newEntries);
    onEntriesChange?.({ entries: newEntries, level: level });
    setReportingLevel(level);
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLevel = e.target.value as "overall" | "category";
    if (newLevel === "category") {
      updateEntries(initialEntries, newLevel);
    } else {
      updateEntries(initialStatusEntries, newLevel);
    }
  };

  const handleNameChange = (index: number, name: string) => {
    const updated = [...entries];
    updated[index].name = name;
    updateEntries(updated, reportingLevel);
  };
  const handleColorChange = (index: number, color: string) => {
    const updated = [...entries];
    updated[index].color = color;
    updateEntries(updated, reportingLevel);
  };
  const togglePicker = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    if (!enableColorPicker) return;
    if (openIndex === index) {
      setOpenIndex(null);
      setAnchorEl(null);
    } else {
      setOpenIndex(index);
      setAnchorEl(event.currentTarget);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        gap: 10,
        width: "100%",
        border: "1px solid #E0E0E0",
        padding: 2,
        borderRadius: "12px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontWeight: 400, minWidth: 280, fontSize: 14 }}>
            {title}
          </Typography>
          <RadioGroup row value={reportingLevel} onChange={handleLevelChange}>
            <FormControlLabel
              value="overall"
              control={<Radio />}
              labelPlacement="start"
              label={
                <Typography sx={{ fontSize: "14px" }}>
                  Overall Status
                </Typography>
              }
            />
            <FormControlLabel
              value="category"
              labelPlacement="start"
              control={<Radio />}
              label={
                <Typography sx={{ fontSize: "14px" }}>
                  Status Categories
                </Typography>
              }
            />
          </RadioGroup>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 15,
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 500, width: 280, fontSize: 14 }}>
            Status Name
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Status Color
          </Typography>
        </Box>

        {reportingLevel === "category"
          ? entries?.map((entry, index) => (
              <Box
                key={entry.label + "semaphore"}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 5,
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 360 }}>
                  <ControlledTextFormField
                    value={entry.name}
                    onChange={(val) => handleNameChange(index, val)}
                    error={Boolean(
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]
                    )}
                    helperText={
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]?.message
                    }
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 4 }}>
                  <Box
                    sx={{
                      height: 30,
                      width: 30,
                      background: theme.palette.success.main,
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      height: 30,
                      width: 30,
                      background: "#F89500",
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      height: 30,
                      width: 30,

                      background: "#FF0000",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
              </Box>
            ))
          : entries?.map((entry, index) => (
              <Box
                key={entry.label + "status"}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 5,
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 360, height: "100%" }}>
                  <Typography
                    sx={{ fontWeight: 500, minWidth: 280, fontSize: 14 }}
                  >
                    {entry.label}
                  </Typography>
                  <ControlledTextFormField
                    placeholder={entry.placeholder}
                    value={entry.name}
                    onChange={(val) => handleNameChange(index, val)}
                    error={Boolean(
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]
                    )}
                    helperText={
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]?.message
                    }
                  />
                </Box>
                <Box>
                  <Box
                    onClick={(e) => togglePicker(e, index)}
                    sx={{
                      height: 30,
                      width: 30,
                      background: entry.color,
                      "&:hover": {
                        background: `${entry.color}50`,
                      },
                      borderRadius: "50%",
                    }}
                  />
                  <Popover
                    open={openIndex === index}
                    anchorEl={anchorEl}
                    onClose={() => setOpenIndex(null)}
                    sx={{ zIndex: 2000, borderRadius: "12px" }}
                  >
                    <HexColorPicker
                      color={entry.color}
                      onChange={(color) => handleColorChange(index, color)}
                    />
                  </Popover>
                </Box>
              </Box>
            ))}
      </Box>
    </Box>
  );
};
